import React, { useEffect, useContext } from "react";
import moment from "moment";
import { get } from "lodash";
import { Card, Row, Col, Typography, Tooltip } from "antd";
import { UserOutlined, CalendarOutlined, IdcardTwoTone } from "@ant-design/icons";
import InfoCard from "./InfoCard";
import { useSmartAssistStore } from "../store";
import { useRootCauseData } from "../../store";
import { CustomerProps } from "../../ShowCustomer";
const { Title, Text } = Typography;

const CustomerCard = () => {
  const { preReadSmartAssist } = useSmartAssistStore();
  const {
    coin,
    coinActivity,
    csat,
    engagement,
    eventData,
    expectationMonth,
    formResponse,
    kitData,
    limitedComments,
    resultExpec,
    ticket,
    orderDetails = [],
  } = preReadSmartAssist;

  const { customerData } = useContext(CustomerProps);
  const { order_meta: orderMeta = {}, engagement_regularity: engagementRegularity = '' } =
    Array.isArray(orderDetails) && orderDetails.length > 0 ? orderDetails[0] : {};

  const { rootCauseData, fetchRootCauseData } = useRootCauseData();

  useEffect(() => {
    const syntheticId = expectationMonth?.value?.syntheticId ?? null;
    if (syntheticId) {
      fetchRootCauseData(syntheticId);
    }
  }, []);

  const rootCause = get(rootCauseData || {}, "root_causes", {
    defaultRootCause: [],
    primaryRootCause: [],
  });

  const combinedRootCause = [
    ...rootCause.defaultRootCause,
    ...rootCause.primaryRootCause
  ];

  const tickets = get(ticket, 'value', []);
  const today = moment();
  const past30Days = moment().subtract(30, 'days');

  // Filter tickets within the date range
  const recentTickets = tickets.filter(ticket => {
    const ticketDate = moment(ticket.createdAt);
    return ticketDate.isBetween(past30Days, today, 'day', '[]');
  });

  return (
    <Card
      style={{ borderRadius: "10px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}
      title={<Title level={4} className="m-0">Customer Overview</Title>}
    >
      <Row gutter={16}>
        <Col span={12}>
          <InfoCard
            icon={<UserOutlined style={{ color: "#1890ff" }} />}
            title="Personal Profile"
            content={
              <>
                <Text strong>Name: </Text>
                {get(customerData, ["user", "first_name"], "N/A")}<br />
                <Text strong>Root Causes: </Text>
                {combinedRootCause?.map((item, index) => (
                  <span key={index}>
                    {item?.title}
                    {index < combinedRootCause.length - 1 && " | "}
                  </span>
                ))} <br />

                <Text strong>Latest Order Count: </Text>
                {orderDetails.length > 0 ? orderDetails[0]?.count ?? "No Orders" : "No Orders"} <br />
                <Text strong>Last 2 Kits: </Text>
                {orderDetails.map((order, index) => {
                  const lineItems = get(order, ['order_meta', 'line_items'], []);
                  return (
                    <>
                      {lineItems.length > 0 ? (
                        <Tooltip
                          color="#2c7be5"
                          key={index}
                          title={
                            <>
                              <p className="mb-2" style={{ fontSize: '10px' }}>
                                <strong>Status:</strong> {get(order, 'status', 'N/A')}
                              </p>
                              <div className="bg-light p-2 rounded">
                                {lineItems.map((item, index) => (
                                  <div key={index} className="mb-3 border-bottom pb-2">
                                    <p style={{ fontSize: "12px" }} className="mb-0 text-black">
                                      {get(item, 'name', 'Unknown Item')} {`(${get(item, 'quantity', 0)})`}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </>
                          }
                        >
                          <span className="text-primary" style={{ fontSize: "12px", marginRight: "10px" }}>
                            {get(order, 'count', 0)}
                            <IdcardTwoTone color="blue" className="ms-1 me-3" />
                          </span>
                        </Tooltip>
                      ) : (
                        <span className="text-muted" style={{ fontSize: "12px", marginRight: "10px" }}>
                          N/A
                        </span>
                      )}
                    </>
                  );
                })}
              </>
            }
          />
        </Col>
        <Col span={12}>
          <InfoCard
            icon={<CalendarOutlined style={{ color: "#52c41a" }} />}
            title="Activity Profile"
            content={
              <>
                <Text strong>Is Customer Regular: </Text> {engagementRegularity} <br />
                <Text strong>Latest ticket: </Text>
                <span>
                  {recentTickets.map((ticket, index) => (
                    <Tooltip
                      color="#2c7be5"
                      key={index}
                      title={
                        <>
                          <p className="mb-2" style={{ fontSize: '10px' }}><strong>Status:</strong> {get(ticket, 'status', 'N/A')}</p>
                          <p className="mb-2" style={{ fontSize: '10px' }}><strong>Category:</strong> {get(ticket, 'category', 'N/A')}</p>
                          <p className="mb-2" style={{ fontSize: '10px' }}><strong>Sub Category:</strong> {get(ticket, 'sub_category', 'N/A')}</p>
                          <p className="mb-2" style={{ fontSize: '10px' }}><strong>Comments:</strong></p>
                          <div className="bg-light p-2 rounded">
                            {get(ticket, 'comments', []).map((comment, index) => (
                              <div key={index} className="mb-3 border-bottom pb-2">
                                <p className="text-muted mb-1" style={{ fontSize: "10px" }}>
                                  {moment(get(comment, 'created_at')).format("YYYY-MM-DD hh:mm:ss A")}
                                </p>
                                <p style={{ fontSize: "12px" }} className="mb-0 text-black">{get(comment, 'description', 'No description available')}</p>
                              </div>
                            ))}
                          </div>
                        </>
                      }
                    >
                      <span className="text-primary" style={{ fontSize: "12px", marginRight: "10px" }}>
                        <IdcardTwoTone color="blue" className="me-2" />
                        {moment(get(ticket, 'createdAt')).format("MMM Do")},
                      </span>
                    </Tooltip>
                  ))}
                </span> <br />
              </>
            }
          />
        </Col>
      </Row>
    </Card>
  )
};



export default CustomerCard;